import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { SharedChecklistPayload } from '@repo/map-user-data/share';
import type { MapDataHeroAction } from '@repo/types/maps/map-types';
import {
    hideAllRegionPins,
    resetAllFilters,
    setCompleteMapData,
    setHideAll,
    showAllRegionPins,
    showOnlyCategoryPins,
    showOnlyRegionPins,
    showOnlySubcategoryPins,
    toggleCategoryFilter,
    toggleRegionPinVisibility,
    toggleSubcategoryFilter,
    toggleTeleportOnly,
    toggleUnfoundOnly,
} from './mapData';

export type SidebarView =
    | 'default'
    | 'sessionHistory'
    | 'checklist'
    | 'explore'
    | 'askAnything'
    | 'customChecklist'
    | 'sharedChecklist'
    | 'heroAction';

export interface ActiveChecklistSubcategory {
    categoryId: string;
    subcategoryId: string;
    subcategoryTitle: string;
    pinId: string;
}

export interface AskAnythingResult {
    anchorPinId: string;
    query: string;
    answer?: string;
    pinIds: string[];
}

export interface ClientContextState {
    isMobileViewport: boolean;
    isCompactViewport: boolean;
    isMiniMapMode: boolean;
    sidebarOpen: boolean;
    autoFocusSearch: boolean;
    activeCategoryId: string | null;
    sidebarView: SidebarView;
    exploreAnchorPinId: string | null;
    exploreActionMenuPinId: string | null;
    compassCtaPinId: string | null;
    teleportCtaPinId: string | null;
    activeChecklistSubcategory: ActiveChecklistSubcategory | null;
    askAnythingResult: AskAnythingResult | null;
    activeCustomChecklistId: string | null;
    sharedChecklistPayload: SharedChecklistPayload | null;
    activeHeroActionId: string | null;
    heroActionTriggerPinId: string | null;
    previewHeroAction: MapDataHeroAction | null;
    highlightedPinIds: string[];
    showTeleportWarning: boolean;
    activeDevelopmentMessageDismissedAt: string | null;
}

const initialState: ClientContextState = {
    isMobileViewport: false,
    isCompactViewport: false,
    isMiniMapMode: false,
    sidebarOpen: true,
    autoFocusSearch: false,
    activeCategoryId: null,
    sidebarView: 'default',
    exploreAnchorPinId: null,
    exploreActionMenuPinId: null,
    compassCtaPinId: null,
    teleportCtaPinId: null,
    activeChecklistSubcategory: null,
    askAnythingResult: null,
    activeCustomChecklistId: null,
    sharedChecklistPayload: null,
    activeHeroActionId: null,
    heroActionTriggerPinId: null,
    previewHeroAction: null,
    highlightedPinIds: [],
    showTeleportWarning: true,
    activeDevelopmentMessageDismissedAt: null,
};

const FILTER_CHANGE_ACTIONS = new Set([
    toggleCategoryFilter.type,
    showOnlyCategoryPins.type,
    showOnlySubcategoryPins.type,
    toggleSubcategoryFilter.type,
    toggleRegionPinVisibility.type,
    showOnlyRegionPins.type,
    showAllRegionPins.type,
    hideAllRegionPins.type,
    setHideAll.type,
    toggleTeleportOnly.type,
    toggleUnfoundOnly.type,
    resetAllFilters.type,
]);

const clearExploreMenuFields = (state: Pick<ClientContextState, 'exploreAnchorPinId' | 'exploreActionMenuPinId'>) => {
    state.exploreAnchorPinId = null;
    state.exploreActionMenuPinId = null;
};

const clearExploreActionFields = (
    state: Pick<
        ClientContextState,
        | 'activeChecklistSubcategory'
        | 'activeHeroActionId'
        | 'askAnythingResult'
        | 'exploreAnchorPinId'
        | 'exploreActionMenuPinId'
        | 'heroActionTriggerPinId'
    >,
) => {
    clearExploreMenuFields(state);
    state.activeChecklistSubcategory = null;
    state.askAnythingResult = null;
    state.activeHeroActionId = null;
    state.heroActionTriggerPinId = null;
};

const clientContextSlice = createSlice({
    name: 'clientContext',
    initialState,
    reducers: {
        updateMobileViewport: (state, action: PayloadAction<boolean>) => {
            state.isMobileViewport = action.payload;
        },
        updateCompactViewport: (state, action: PayloadAction<boolean>) => {
            state.isCompactViewport = action.payload;
        },
        updateMiniMapMode: (state, action: PayloadAction<boolean>) => {
            state.isMiniMapMode = action.payload;
        },
        setSidebarOpen: (state, action: PayloadAction<boolean>) => {
            state.sidebarOpen = action.payload;
        },
        setAutoFocusSearch: (state, action: PayloadAction<boolean>) => {
            state.autoFocusSearch = action.payload;
        },
        setActiveCategoryId: (state, action: PayloadAction<string | null>) => {
            state.activeCategoryId = action.payload;
        },
        setSidebarView: (state, action: PayloadAction<SidebarView>) => {
            state.sidebarView = action.payload;
        },
        setExploreAnchorPinId: (state, action: PayloadAction<string | null>) => {
            state.exploreAnchorPinId = action.payload;
        },
        setExploreActionMenuPinId: (state, action: PayloadAction<string | null>) => {
            state.exploreActionMenuPinId = action.payload;
        },
        setCompassCtaPinId: (state, action: PayloadAction<string | null>) => {
            state.compassCtaPinId = action.payload;
        },
        setTeleportCtaPinId: (state, action: PayloadAction<string | null>) => {
            state.teleportCtaPinId = action.payload;
        },
        clearExploreMenuState: state => {
            clearExploreMenuFields(state);
        },
        clearExploreActionState: state => {
            clearExploreActionFields(state);
            state.compassCtaPinId = null;
            state.teleportCtaPinId = null;
        },
        setActiveChecklistSubcategory: (state, action: PayloadAction<ActiveChecklistSubcategory | null>) => {
            state.activeChecklistSubcategory = action.payload;
        },
        setAskAnythingResult: (state, action: PayloadAction<AskAnythingResult | null>) => {
            state.askAnythingResult = action.payload;
        },
        setActiveCustomChecklistId: (state, action: PayloadAction<string | null>) => {
            state.activeCustomChecklistId = action.payload;
        },
        setSharedChecklistPayload: (state, action: PayloadAction<SharedChecklistPayload | null>) => {
            state.sharedChecklistPayload = action.payload;
        },
        setActiveHeroActionId: (state, action: PayloadAction<string | null>) => {
            if (state.activeHeroActionId === action.payload) return;
            state.activeHeroActionId = action.payload;
        },
        setHeroActionTriggerPinId: (state, action: PayloadAction<string | null>) => {
            if (state.heroActionTriggerPinId === action.payload) return;
            state.heroActionTriggerPinId = action.payload;
        },
        setPreviewHeroAction: (state, action: PayloadAction<MapDataHeroAction | null>) => {
            const heroAction = action.payload;
            state.previewHeroAction = heroAction
                ? {
                      externalId: heroAction.externalId,
                      name: heroAction.name,
                      description: heroAction.description,
                      iconCategory: heroAction.iconCategory,
                      priority: heroAction.priority,
                      isApproved: heroAction.isApproved,
                      resultPins: cloneGroupedPinIds(heroAction.resultPins),
                      triggerPins: cloneGroupedPinIds(heroAction.triggerPins),
                  }
                : null;
        },
        setHighlightedPinIds: (state, action: PayloadAction<string[]>) => {
            state.highlightedPinIds = action.payload;
        },
        setShowTeleportWarning: (state, action: PayloadAction<boolean>) => {
            state.showTeleportWarning = action.payload;
        },
        setActiveDevelopmentMessageDismissedAt: (state, action: PayloadAction<string | null>) => {
            state.activeDevelopmentMessageDismissedAt = action.payload;
        },
    },
    extraReducers: builder => {
        builder.addCase(setCompleteMapData, state => {
            if (
                state.sidebarView === 'explore' ||
                state.sidebarView === 'askAnything' ||
                state.sidebarView === 'customChecklist' ||
                state.sidebarView === 'sharedChecklist' ||
                state.sidebarView === 'heroAction'
            ) {
                state.sidebarView = 'default';
            }
            clearExploreActionFields(state);
            state.teleportCtaPinId = null;
            state.activeCustomChecklistId = null;
            state.sharedChecklistPayload = null;
            state.previewHeroAction = null;
            state.highlightedPinIds = [];
        });
        builder.addMatcher(
            (action): action is { type: string } => FILTER_CHANGE_ACTIONS.has(action.type),
            state => {
                state.activeCategoryId = null;
            },
        );
    },
});

export const {
    updateMobileViewport,
    updateCompactViewport,
    updateMiniMapMode,
    setSidebarOpen,
    setAutoFocusSearch,
    setActiveCategoryId,
    setSidebarView,
    setExploreAnchorPinId,
    setExploreActionMenuPinId,
    setCompassCtaPinId,
    setTeleportCtaPinId,
    clearExploreMenuState,
    clearExploreActionState,
    setActiveChecklistSubcategory,
    setAskAnythingResult,
    setActiveCustomChecklistId,
    setSharedChecklistPayload,
    setActiveHeroActionId,
    setHeroActionTriggerPinId,
    setPreviewHeroAction,
    setHighlightedPinIds,
    setShowTeleportWarning,
    setActiveDevelopmentMessageDismissedAt,
} = clientContextSlice.actions;

export default clientContextSlice.reducer;

export const selectIsMobileViewport = (state: { clientContext: ClientContextState }) =>
    state.clientContext.isMobileViewport;

export const selectIsCompactViewport = (state: { clientContext: ClientContextState }) =>
    state.clientContext.isCompactViewport;

export const selectIsMiniMapMode = (state: { clientContext: ClientContextState }) => state.clientContext.isMiniMapMode;

export const selectSidebarOpen = (state: { clientContext: ClientContextState }) => state.clientContext.sidebarOpen;

export const selectAutoFocusSearch = (state: { clientContext: ClientContextState }) =>
    state.clientContext.autoFocusSearch;

export const selectActiveCategoryId = (state: { clientContext: ClientContextState }) =>
    state.clientContext.activeCategoryId;

export const selectSidebarView = (state: { clientContext: ClientContextState }): SidebarView =>
    state.clientContext.sidebarView ?? 'default';

export const selectExploreAnchorPinId = (state: { clientContext: ClientContextState }) =>
    state.clientContext.exploreAnchorPinId ?? null;

export const selectExploreActionMenuPinId = (state: { clientContext: ClientContextState }) =>
    state.clientContext.exploreActionMenuPinId ?? null;

export const selectCompassCtaPinId = (state: { clientContext: ClientContextState }) =>
    state.clientContext.compassCtaPinId ?? null;

export const selectTeleportCtaPinId = (state: { clientContext: ClientContextState }) =>
    state.clientContext.teleportCtaPinId ?? null;

export const selectActiveChecklistSubcategory = (state: { clientContext: ClientContextState }) =>
    state.clientContext.activeChecklistSubcategory ?? null;

export const selectAskAnythingResult = (state: { clientContext: ClientContextState }) =>
    state.clientContext.askAnythingResult ?? null;

export const selectActiveCustomChecklistId = (state: { clientContext: ClientContextState }) =>
    state.clientContext.activeCustomChecklistId ?? null;

export const selectSharedChecklistPayload = (state: { clientContext: ClientContextState }) =>
    state.clientContext.sharedChecklistPayload ?? null;

export const selectActiveHeroActionId = (state: { clientContext: ClientContextState }) =>
    state.clientContext.activeHeroActionId ?? null;

export const selectHeroActionTriggerPinId = (state: { clientContext: ClientContextState }) =>
    state.clientContext.heroActionTriggerPinId ?? null;

export const selectPreviewHeroAction = (state: { clientContext: ClientContextState }) =>
    state.clientContext.previewHeroAction ?? null;

// Frozen empty array reused across selector calls so React/redux equality checks don't
// see a fresh `[]` every time the SSR preloadedState omits this field.
const EMPTY_STRING_ARRAY = Object.freeze<string[]>([]) as unknown as string[];

export const selectHighlightedPinIds = (state: { clientContext: ClientContextState }): string[] =>
    state.clientContext.highlightedPinIds ?? EMPTY_STRING_ARRAY;

export const selectShowTeleportWarning = (state: { clientContext: ClientContextState }): boolean =>
    state.clientContext.showTeleportWarning ?? true;

export const selectActiveDevelopmentMessageDismissedAt = (state: { clientContext: ClientContextState }) =>
    state.clientContext.activeDevelopmentMessageDismissedAt ?? null;

function cloneGroupedPinIds(groupedPins: Readonly<Record<string, ReadonlyArray<string>>>): Record<string, string[]> {
    return Object.fromEntries(Object.entries(groupedPins).map(([mapId, pinIds]) => [mapId, [...pinIds]]));
}
